import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tags, ArrowLeft, Plus, Trash2, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCaseCategories = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', type: 'industry' });

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'operator') {
      navigate('/create');
      return;
    }
    fetchCategories();
  }, [user, navigate]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/case-library/admin/categories`);
      setCategories(response.data);
    } catch (error) {
      toast.error('獲取分類失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name) {
      toast.error('請填寫分類名稱');
      return;
    }

    try {
      await axios.post(`${API_URL}/api/case-library/admin/categories`, formData);
      toast.success('分類創建成功！');
      setShowCreateForm(false);
      setFormData({ name: '', type: 'industry' });
      fetchCategories();
    } catch (error) {
      toast.error('創建失敗');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`確定要刪除「${name}」嗎？`)) return;

    try {
      await axios.delete(`${API_URL}/api/case-library/admin/categories/${id}`);
      toast.success('刪除成功！');
      fetchCategories();
    } catch (error) {
      toast.error('刪除失敗');
    }
  };

  const getCategoryTypeName = (type) => {
    const names = {
      industry: '行業',
      video_type: '影片類別',
      platform: '平台'
    };
    return names[type] || type;
  };

  const groupedCategories = {
    industry: categories.filter(c => c.type === 'industry'),
    video_type: categories.filter(c => c.type === 'video_type'),
    platform: categories.filter(c => c.type === 'platform')
  };

  if (user?.role !== 'admin' && user?.role !== 'operator') return null;

  return (
    <>
      <header className="bg-white border-b border-neutral-200 px-8 py-6 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-neutral-900">分類管理</h1>
            <p className="text-neutral-600 text-sm mt-1">管理案例的分類標籤</p>
          </div>
          <Button onClick={() => setShowCreateForm(true)} className="rounded-full">
            <Plus className="w-4 h-4 mr-2" />
            新增分類
          </Button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-8">
        {showCreateForm && (
          <Card className="p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-heading font-bold">新增分類</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowCreateForm(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-4">
              <div>
                <Label>分類名稱 *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="例：美妝、故事型、抖音"
                />
              </div>
              <div>
                <Label>分類類型 *</Label>
                <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                  <SelectTrigger className="bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="industry" className="bg-white hover:bg-blue-50">行業</SelectItem>
                    <SelectItem value="video_type" className="bg-white hover:bg-blue-50">影片類別</SelectItem>
                    <SelectItem value="platform" className="bg-white hover:bg-blue-50">平台</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreate} className="rounded-full">
                  <Save className="w-4 h-4 mr-2" />
                  儲存
                </Button>
                <Button variant="outline" onClick={() => setShowCreateForm(false)} className="rounded-full">
                  取消
                </Button>
              </div>
            </div>
          </Card>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-neutral-600">載入中...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedCategories).map(([type, cats]) => (
              <div key={type}>
                <h2 className="text-lg font-heading font-bold text-neutral-900 mb-3">
                  {getCategoryTypeName(type)}
                </h2>
                {cats.length === 0 ? (
                  <Card className="p-8 text-center border-dashed">
                    <p className="text-neutral-500">尚無{getCategoryTypeName(type)}分類</p>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {cats.map((cat) => (
                      <Card key={cat.id} className="p-4 flex items-center justify-between">
                        <span className="font-medium text-neutral-900">{cat.name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(cat.id, cat.name)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </>
  );
};

export default AdminCaseCategories;
